import type { AccountingAccountReport } from '@/lib/api/accounting';
import { getAccountingPrintSettings } from '@/lib/printSettings';

export type AccountTurnoverExcelOptions = {
  report: AccountingAccountReport;
  accountTitle: string;
  projectNameMap: Record<number, string>;
  fromDate?: string;
  toDate?: string;
};

const FONT = 'Tahoma';
const NUM_FMT = '#,##0';

function todayFaLabel(): string {
  try {
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toLocaleString('fa-IR');
  }
}

function setFill(
  cell: { fill?: unknown },
  argb: string,
) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb },
  };
}

function thinBorder() {
  return {
    top: { style: 'thin' as const },
    left: { style: 'thin' as const },
    bottom: { style: 'thin' as const },
    right: { style: 'thin' as const },
  };
}

export async function exportAccountTurnoverExcel(opts: AccountTurnoverExcelOptions): Promise<void> {
  const { report, accountTitle, projectNameMap, fromDate, toDate } = opts;
  const companyName = getAccountingPrintSettings().companyName;
  const includeSub = Boolean(report.includeSubAccounts);

  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Darvag Accounting';
  wb.created = new Date();

  const ws = wb.addWorksheet('گردش حساب', {
    views: [{ rightToLeft: true, showGridLines: true }],
    properties: { defaultRowHeight: 20 },
  });

  const colCount = includeSub ? 10 : 9;

  const titleFill = 'FF0F4C75';
  const subtitleFill = 'FFE3F2FD';
  const metaFill = 'FFF5F5F5';
  const headerFill = 'FF1565C0';
  const totalFill = 'FFE8F5E9';
  const altFill = 'FFF8FAFC';

  let row = 1;

  ws.mergeCells(row, 1, row, colCount);
  const titleCell = ws.getCell(row, 1);
  titleCell.value = companyName;
  titleCell.font = { name: FONT, size: 18, bold: true, color: { argb: 'FFFFFFFF' } };
  setFill(titleCell, titleFill);
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(row).height = 32;
  row += 1;

  ws.mergeCells(row, 1, row, colCount);
  const subTitle = ws.getCell(row, 1);
  subTitle.value = 'گزارش گردش حساب';
  subTitle.font = { name: FONT, size: 14, bold: true, color: { argb: 'FF0F4C75' } };
  setFill(subTitle, subtitleFill);
  subTitle.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(row).height = 26;
  row += 1;

  const periodLabel =
    fromDate || toDate
      ? `بازه: ${fromDate || '—'} تا ${toDate || '—'}`
      : 'بازه: کل دوره (بدون محدودیت تاریخ)';

  ws.mergeCells(row, 1, row, colCount);
  const periodCell = ws.getCell(row, 1);
  periodCell.value = periodLabel;
  periodCell.font = { name: FONT, size: 11, color: { argb: 'FF455A64' } };
  setFill(periodCell, metaFill);
  periodCell.alignment = { horizontal: 'center', vertical: 'middle' };
  row += 1;

  ws.mergeCells(row, 1, row, colCount);
  const accountCell = ws.getCell(row, 1);
  accountCell.value = `حساب: ${report.accountCode} — ${accountTitle || report.accountTitle || '—'}${
    includeSub ? ' (شامل زیرمجموعه‌ها)' : ''
  }`;
  accountCell.font = { name: FONT, size: 11, bold: true };
  setFill(accountCell, metaFill);
  accountCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  ws.getRow(row).height = 22;
  row += 1;

  ws.mergeCells(row, 1, row, colCount);
  ws.getCell(row, 1).value = `تاریخ تهیه: ${todayFaLabel()}`;
  ws.getCell(row, 1).font = { name: FONT, size: 10, color: { argb: 'FF78909C' } };
  ws.getCell(row, 1).alignment = { horizontal: 'center', vertical: 'middle' };
  row += 1;

  row += 1;

  const summaryStart = row;
  const summaryLabels = ['جمع بدهکار', 'جمع بستانکار', 'مانده نهایی'];
  const summaryValues = [report.totalDebit || 0, report.totalCredit || 0, report.balance || 0];

  for (let i = 0; i < 3; i++) {
    ws.mergeCells(row, 1, row, 2);
    const labelCell = ws.getCell(row, 1);
    labelCell.value = summaryLabels[i];
    labelCell.font = { name: FONT, size: 11, bold: true };
    setFill(labelCell, subtitleFill);
    labelCell.alignment = { horizontal: 'right', vertical: 'middle' };
    labelCell.border = thinBorder();

    ws.mergeCells(row, 3, row, colCount);
    const valCell = ws.getCell(row, 3);
    valCell.value = summaryValues[i];
    valCell.numFmt = NUM_FMT;
    valCell.font = { name: FONT, size: 12, bold: true };
    setFill(valCell, i === 2 ? totalFill : 'FFFFFFFF');
    valCell.alignment = { horizontal: 'left', vertical: 'middle' };
    valCell.border = thinBorder();
    row += 1;
  }

  for (let r = summaryStart; r < summaryStart + 3; r++) {
    for (let c = 1; c <= colCount; c++) {
      const cell = ws.getCell(r, c);
      if (!cell.border) cell.border = thinBorder();
    }
  }

  row += 1;

  const headers = [
    'ردیف',
    'شماره سند',
    'تاریخ',
    'شرح عملیات',
    'پروژه',
    ...(includeSub ? ['کد حساب'] : []),
    'عنوان حساب',
    'بدهکار',
    'بستانکار',
    'مانده جاری',
  ];

  const headerRow = ws.getRow(row);
  headerRow.height = 24;
  headers.forEach((h, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = h;
    cell.font = { name: FONT, size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    setFill(cell, headerFill);
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = thinBorder();
  });
  const headerRowIndex = row;
  row += 1;

  let runningBalance = 0;
  const dataStartRow = row;

  if (report.rows.length === 0) {
    ws.mergeCells(row, 1, row, colCount);
    const emptyCell = ws.getCell(row, 1);
    emptyCell.value = 'گردشی برای این حساب یافت نشد.';
    emptyCell.font = { name: FONT, size: 11, italic: true, color: { argb: 'FF78909C' } };
    emptyCell.alignment = { horizontal: 'center', vertical: 'middle' };
    row += 1;
  } else {
    report.rows.forEach((r, index) => {
      const debit = Number(r.debit || 0);
      const credit = Number(r.credit || 0);
      runningBalance += debit - credit;

      const dataRow = ws.getRow(row);
      const isAlt = index % 2 === 1;
      const values: (string | number)[] = [
        index + 1,
        r.docNo,
        r.docDate,
        r.operationDescription || '—',
        (r.projectId && projectNameMap[r.projectId]) || '—',
        ...(includeSub ? [r.accountCode] : []),
        r.accountTitle || '—',
        debit || '',
        credit || '',
        runningBalance,
      ];

      values.forEach((val, colIdx) => {
        const cell = dataRow.getCell(colIdx + 1);
        cell.value = val;
        cell.font = { name: FONT, size: 10 };
        cell.border = thinBorder();
        if (isAlt) setFill(cell, altFill);

        const colHeader = headers[colIdx];
        if (colHeader === 'بدهکار' || colHeader === 'بستانکار' || colHeader === 'مانده جاری') {
          if (typeof val === 'number') {
            cell.numFmt = NUM_FMT;
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          }
        } else if (colHeader === 'ردیف' || colHeader === 'شماره سند' || colHeader === 'کد حساب') {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'right', vertical: 'middle', wrapText: true };
        }
      });
      row += 1;
    });
  }

  const totalRow = ws.getRow(row);
  totalRow.height = 22;

  const totalLabelCol = includeSub ? 7 : 6;
  ws.mergeCells(row, 1, row, totalLabelCol);
  const totalLabel = ws.getCell(row, 1);
  totalLabel.value = 'جمع کل';
  totalLabel.font = { name: FONT, size: 11, bold: true };
  setFill(totalLabel, totalFill);
  totalLabel.alignment = { horizontal: 'right', vertical: 'middle' };
  totalLabel.border = thinBorder();

  const debitCol = includeSub ? 8 : 7;
  const creditCol = includeSub ? 9 : 8;
  const balanceCol = includeSub ? 10 : 9;

  const totalDebitCell = totalRow.getCell(debitCol);
  totalDebitCell.value = report.totalDebit || 0;
  totalDebitCell.numFmt = NUM_FMT;
  totalDebitCell.font = { name: FONT, size: 11, bold: true };
  setFill(totalDebitCell, totalFill);
  totalDebitCell.border = thinBorder();
  totalDebitCell.alignment = { horizontal: 'left', vertical: 'middle' };

  const totalCreditCell = totalRow.getCell(creditCol);
  totalCreditCell.value = report.totalCredit || 0;
  totalCreditCell.numFmt = NUM_FMT;
  totalCreditCell.font = { name: FONT, size: 11, bold: true };
  setFill(totalCreditCell, totalFill);
  totalCreditCell.border = thinBorder();
  totalCreditCell.alignment = { horizontal: 'left', vertical: 'middle' };

  const totalBalanceCell = totalRow.getCell(balanceCol);
  totalBalanceCell.value = report.balance || 0;
  totalBalanceCell.numFmt = NUM_FMT;
  totalBalanceCell.font = { name: FONT, size: 11, bold: true };
  setFill(totalBalanceCell, totalFill);
  totalBalanceCell.border = thinBorder();
  totalBalanceCell.alignment = { horizontal: 'left', vertical: 'middle' };

  ws.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: Math.max(headerRowIndex, row - 1), column: colCount },
  };

  if (report.rows.length > 0) {
    ws.views = [{ state: 'frozen', ySplit: headerRowIndex, rightToLeft: true, activeCell: `A${dataStartRow}` }];
  }

  const widths = includeSub
    ? [6, 12, 12, 28, 22, 12, 22, 14, 14, 14]
    : [6, 12, 12, 30, 24, 24, 14, 14, 14];
  widths.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const fileDate = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `account-turnover-${report.accountCode}-${fileDate}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
